import { useState } from 'react';
import { Box, Typography, Chip, Link, Collapse, IconButton, Stack } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReactMarkdown from 'react-markdown';
import { toKstDate, formatDate, releaseKind, productColor } from '../../utils/releaseView';

// FEAT-023 — 릴리즈 한 줄. 전사 화면과 제품 상세 탭이 같은 줄을 쓴다.
//
// 노트 본문은 기본으로 접는다. 평균 829자라 다 펼치면 목록이 아니라 문서가 되고,
// "언제 무엇이 나갔나" 를 훑는 목적이 사라진다.

export default function ReleaseRow({ release, productNames, showProduct = true, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const date = toKstDate(release.published);
  const hasBody = !!(release.body || '').trim();
  const prods = release.products || [];

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          py: 0.9,
          px: 1,
          cursor: hasBody ? 'pointer' : 'default',
          '&:hover': hasBody ? { bgcolor: 'action.hover' } : undefined,
        }}
        onClick={() => hasBody && setOpen((v) => !v)}
      >
        <IconButton size="small" disabled={!hasBody} sx={{ p: 0.25 }} tabIndex={-1}>
          {hasBody ? (open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />) : null}
        </IconButton>

        <Typography variant="caption" sx={{ minWidth: 88, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
          {formatDate(date)}
        </Typography>

        {showProduct && (
          <Box sx={{ minWidth: 150 }}>
            {prods.length > 0 ? (
              prods.map((pid) => (
                <Chip
                  key={pid}
                  size="small"
                  label={productNames?.get(pid) || pid}
                  sx={{
                    mr: 0.5,
                    height: 20,
                    fontSize: 11,
                    bgcolor: `${productColor(pid)}22`,
                    borderLeft: `3px solid ${productColor(pid)}`,
                    borderRadius: '3px',
                  }}
                />
              ))
            ) : (
              <Chip size="small" label="제품 미지정" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
            )}
          </Box>
        )}

        <Typography
          variant="body2"
          sx={{ fontFamily: 'JetBrains Mono, monospace', minWidth: 180, color: 'text.secondary' }}
        >
          {release.repo}
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 110 }}>
          {release.tag}
        </Typography>

        {release.prerelease && (
          <Chip size="small" label={releaseKind(release)} color="warning" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
        )}

        <Box sx={{ flex: 1 }} />

        <Link
          href={release.url}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          sx={{ display: 'flex', alignItems: 'center', fontSize: 12 }}
        >
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
      </Stack>

      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            px: 4,
            pb: 1.5,
            fontSize: 13.5,
            lineHeight: 1.7,
            '& h1, & h2, & h3': { fontSize: 14, fontWeight: 700, mt: 1.5, mb: 0.5 },
            '& ul': { pl: 2.5, my: 0.5 },
            '& li': { mb: 0.25 },
            '& p': { my: 0.5 },
            '& code': { fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5, bgcolor: 'action.hover', px: 0.5, borderRadius: '3px' },
          }}
        >
          <ReactMarkdown>{release.body}</ReactMarkdown>
          {release.body_truncated && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              … 본문이 길어 일부만 표시합니다 — 전문은 우측 링크에서 보세요.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
